import { createColumnHelper } from '@tanstack/react-table';
import { ConnectedEventInfo } from 'src/components/Events/ConnectedEventInfo';
import {
  EventDateDisplay,
  VenueNameDisplay,
} from 'src/components/Events/EventInfo';
import { BaseCell } from 'src/components/TableV2/cells';
import {
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_DEFAULT_SIZE_XXL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOption, FormatOptionEntries } from 'src/modals/EditTableColumns';
import * as tableStyles from 'src/tables/Table/Table.css';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { SalesTableFlattenedColumnId } from 'src/utils/columns/sales/salesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import {
  compareEventsByDate,
  compareEventsByName,
  getDescription,
} from 'src/utils/eventWithDataUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { SectionType } from 'src/utils/types/sectionType';
import { DateTimeRange, UserSetting, Venue } from 'src/WebApiController';

import {
  SaleWithEvent,
  SHIMMERING_DIV_HEIGHT_SINGLE_SALE,
} from '../SalesTable.type';
const columnHelper = createColumnHelper<SaleWithEvent | null>();

export const SALE_TABLE_FLATTENED_COLUMNS_CONFIG_v2 = {
  [SalesTableFlattenedColumnId.Event]: columnHelper.accessor((data) => data, {
    id: SalesTableFlattenedColumnId.Event,
    header: () => (
      <TableCellDiv align="left">
        <Content id={ContentId.Event} />
      </TableCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_XXL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, table }) {
      const sale = getValue<SaleWithEvent>();
      const { value: storedColumnNumberPrecisions = {} } =
        useServerUserSetting<FormatOptionEntries>({
          id: UserSetting.SaleFlattenedColumnNumberPrecision,
        });

      if (!sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!sale.event) {
        return null;
      }

      const customPrecision =
        table.options.meta?.formatOptionOverrides?.[
          SalesTableFlattenedColumnId.Event
        ];

      return (
        <BaseCell>
          <ConnectedEventInfo
            showYear={true}
            showEventDateTbd
            event={sale.event}
            performer={sale.performer}
            venue={sale.venue}
            eventNameOnly
            slimMode
            useShortName
            showEventStatus
          />
        </BaseCell>
      );
    },
    sortingFn: compareEventsByName,
  }),
  [SalesTableFlattenedColumnId.Venue]: columnHelper.accessor(
    (data) => data?.venue,
    {
      id: SalesTableFlattenedColumnId.Venue,
      header: () => (
        <TableCellDiv align="left">
          <Content id={ContentId.Venue} />
        </TableCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        const venue = getValue<Venue>();
        if (!original || !original?.sale) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }
        return (
          <BaseCell title={venue?.name} className={tableStyles.basicFont}>
            <VenueNameDisplay
              venueName={venue?.shortName || venue?.name}
              slimMode
              showTitle={false}
            />
          </BaseCell>
        );
      },
    }
  ),
  [SalesTableFlattenedColumnId.Location]: columnHelper.accessor(
    (data) => data,
    {
      id: SalesTableFlattenedColumnId.Location,
      header: () => (
        <TableCellDiv align="left">
          <Content id={ContentId.Location} />
        </TableCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_XL,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        const sale = getValue<SaleWithEvent>();
        if (!original || !original?.sale) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }
        return (
          <BaseCell className={tableStyles.basicFont}>
            <VenueNameDisplay
              venueLocation={
                sale.isFlattenedMode
                  ? getDescription(sale.venue)
                  : sale.venue?.ctryCode
              }
              slimMode
            />
          </BaseCell>
        );
      },
    }
  ),
  [SalesTableFlattenedColumnId.EventDate]: columnHelper.accessor(
    (data) => data?.event?.dates,
    {
      id: SalesTableFlattenedColumnId.EventDate,
      header: () => (
        <TableCellDiv align="left">
          <Content id={ContentId.EventDate} />
        </TableCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original }, column, table }) {
        const columnId = column.id;

        const value = getValue<DateTimeRange | undefined>();
        const {
          userDefinedPrecision = FormatOption.DateTime_MonthDD_Time_Day,
        } = useColumnUserSetting(columnId, SectionType.Events);

        if (!original || !original?.sale) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const customPrecision =
          table.options.meta?.formatOptionOverrides?.[
            SalesTableFlattenedColumnId.EventDate
          ];

        const date = applyNumberFormatting({
          precision: customPrecision ?? userDefinedPrecision,
          inputNumber: value?.start,
          isDateTime: true,
        }) as string;

        return (
          <BaseCell>
            {userDefinedPrecision === FormatOption.DateTime_MonthDD_Time ? (
              <EventDateDisplay
                eventDateString={value?.start ?? undefined}
                eventEndDateString={value?.end ?? undefined}
                showEventDateTbd={true}
                eventSubDateFormat={' - h:mm aaa, eee'}
                slimMode
                className={tableStyles.basicFont}
              />
            ) : (
              <div
                style={{
                  textTransform: 'none',
                }}
              >
                {date}
              </div>
            )}
          </BaseCell>
        );
      },
      sortingFn: compareEventsByDate,
    }
  ),
} as const;
